import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import RscLogo from "../../assests/logo/rscLogo";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetPassword } from "../../reducers/authentication.slice";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import CustomLoader from "../../components/CustomLoader";
import { AuthContext } from "../../context/authContext";

const ResetPasswordWrapper = styled(Stack)(({ theme }) => ({
  minHeight: "100vh", // This ensures it takes at least full viewport height
  height: "100%", // This can make sure the element fills the screen
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#000000",
  overflow: "auto", // Ensure content doesn't overflow outside the viewport
}));

const ResetPageComponent = styled(Stack)(({ theme }) => ({
  padding: "24px",
  backgroundColor: "#101010",
  borderRadius: "10px",
  border: "1px solid #ffffff85",
  width: "370px",
  [theme.breakpoints.down("md")]: {
    width: "480px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "320px",
    padding: "16px",
  },
  [theme.breakpoints.down("xs")]: {
    width: "95%",
    padding: "16px",
  },
}));

const LogoSectionWrapper = styled(Stack)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  flexDirection: "row",
}));

const LogoDisplaySection = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: "white",
  width: "fit-content",
  borderRadius: "3px",
  marginBottom: "20px",
}));

const CustomFormLabel = styled(FormLabel)(({ theme }) => ({
  color: "white",
  marginBottom: "8px",
  fontFamily: "Inter",
  letterSpacing: "0.8px",
  marginTop: "10px",
}));

const ValidationSection = styled(Stack)(({ theme, image }) => ({
  gap: 2,
  flexDirection: "row",
}));

const ResetPasswordPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLengthValid, setIsLengthValid] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasUpperCase, setHasUpperCase] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const [email, setEmail] = useState("");
  const [emailVid, setEmailVid] = useState("");

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { showToast } = useContext(AuthContext);

  useEffect(() => {
    console.log(location.state);
    setEmailVid(location.state?.emailVid);
    setEmail(location.state?.email);
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    const lengthValid =
      e.target.value.length >= 8 && e.target.value.length <= 125;
    setIsLengthValid(lengthValid);

    const numberValid = /\d/.test(e.target.value);
    setHasNumber(numberValid);

    const upperCaseValid = /[A-Z]/.test(e.target.value);
    setHasUpperCase(upperCaseValid);

    const specialCharValid = /[!@#$%^&*(),.?":{}|<>]/.test(e.target.value);
    setHasSpecialChar(specialCharValid);
  };

  const handleResetPassword = () => {
    setShowLoader(true);
    const payload = {
      id: emailVid,
      password: newPassword,
      mode: "RESET",
    };
    dispatch(
      resetPassword({ payload, access_token: location?.state?.access_token })
    ).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      setShowLoader(false);
      if (location.state.isLogin) {
        showToast("Password reset successfully", "success");
      } else {
        navigate("/login");
      }
    });
  };

  return (
    <>
      <CustomLoader open={showLoader} />
      <ResetPasswordWrapper>
        <ResetPageComponent>
          <LogoSectionWrapper>
            <LogoDisplaySection>
              <RscLogo color="black" />
            </LogoDisplaySection>
            {activeStep === 1 && (
              <Stack
                gap={1}
                flexDirection={"row"}
                alignContent={"center"}
                color={"#ffffff85"}
                sx={{ cursor: "pointer" }}
                onClick={() => setActiveStep(0)}
              >
                <KeyboardBackspaceIcon />{" "}
                <Typography fontFamily={"inter"}>Back</Typography>
              </Stack>
            )}
          </LogoSectionWrapper>
          <Typography
            sx={{
              fontSize: "1.7rem",
              color: "white",
              fontWeight: 700,
            }}
          >
            Reset Password?{" "}
          </Typography>
          <Typography
            sx={{
              color: "#ffffff85",
              fontFamily: "Inter",
              fontWeight: 300,
              fontSize: "0.8rem",
            }}
          >
            {email}
          </Typography>
          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <CustomFormLabel htmlFor="lastName">New Password</CustomFormLabel>
            <TextField
              type={showPassword ? "text" : "password"}
              value={newPassword}
              sx={{ marginBottom: 1 }}
              placeholder="Password"
              onChange={handleNewPasswordChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ marginRight: 3 }}>
                    <IconButton
                      aria-label={
                        showPassword ? "hide the password" : "show the password"
                      }
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? (
                        <Visibility sx={{ color: "#ffffff85" }} />
                      ) : (
                        <VisibilityOff sx={{ color: "#ffffff85" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <Stack sx={{ gap: 2, marginTop: 3 }}>
            <ValidationSection>
              <CheckCircleIcon
                sx={{ color: !isLengthValid ? "#ffffff85" : "#4CAF50" }}
              />{" "}
              <Typography sx={{ color: "#ffffff85", ml: 1 }}>
                {" "}
                8 to 125 Characters
              </Typography>
            </ValidationSection>
            <ValidationSection>
              <CheckCircleIcon
                sx={{ color: !hasNumber ? "#ffffff85" : "#4CAF50" }}
              />{" "}
              <Typography sx={{ color: "#ffffff85", ml: 1 }}>
                {" "}
                At least 1 number{" "}
              </Typography>
            </ValidationSection>
            <ValidationSection>
              <CheckCircleIcon
                sx={{ color: !hasUpperCase ? "#ffffff85" : "#4CAF50" }}
              />{" "}
              <Typography sx={{ color: "#ffffff85", ml: 1 }}>
                {" "}
                At least 1 uppercase letter{" "}
              </Typography>
            </ValidationSection>
            <ValidationSection>
              <CheckCircleIcon
                sx={{ color: !hasSpecialChar ? "#ffffff85" : "#4CAF50" }}
              />{" "}
              <Typography sx={{ color: "#ffffff85", ml: 1 }}>
                {" "}
                At least 1 Special Characters{" "}
              </Typography>
            </ValidationSection>
          </Stack>
          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <CustomFormLabel htmlFor="lastName">
              Confirm Password
            </CustomFormLabel>
            <TextField
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              sx={{ marginBottom: 1 }}
              placeholder="Password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ marginRight: 3 }}>
                    <IconButton
                      aria-label={
                        showPassword ? "hide the password" : "show the password"
                      }
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? (
                        <Visibility sx={{ color: "#ffffff85" }} />
                      ) : (
                        <VisibilityOff sx={{ color: "#ffffff85" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <Button
            disabled={
              !isLengthValid ||
              !hasNumber ||
              !hasUpperCase ||
              newPassword !== confirmPassword
            }
            variant="contained"
            onClick={handleResetPassword}
          >
            Confirm
          </Button>
        </ResetPageComponent>
      </ResetPasswordWrapper>
    </>
  );
};

export default ResetPasswordPage;
